import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Plus, Search, Edit3, Trash2, FileText, Sparkles, Loader2, ExternalLink,
  Upload, ShieldCheck, AlertTriangle, Clock, CheckCircle2, Archive,
  GitBranch, RefreshCw, Brain,
} from "lucide-react";
import AdminShell from "../components/AdminShell";
import { useI18n } from "../i18n/I18nContext";
import { api } from "../lib/nailApi";
import {
  Toast, FieldInput, FieldTextarea, FieldSelect,
  FieldToggle, DialogShell, ConfirmDialog,
} from "../components/AdminUI";

const TOKEN_KEY = "nailEvent.adminToken";

const EMPTY = {
  title: "", summary: "", content: "", source_url: "", file_url: "",
  doc_type: "pdf", category: "taxes", subcategory: "",
  tags: "", search_keywords: "", language: "vi",
  author: "", publisher: "", source: "", version: "", effective_date: "", review_date: "",
  status: "draft", visible_public: false, visible_ai: true, visible_members: false,
  featured: false, download_allowed: false, collection_id: "",
  lifecycle_status: "draft",
};

const LIFECYCLE = [
  { slug: "draft",         label_vi: "Bản thảo",       label_en: "Draft",        cls: "bg-warmgray-100 text-warmgray-700" },
  { slug: "under_review",  label_vi: "Đang rà soát",   label_en: "Under Review", cls: "bg-amber-100 text-amber-800" },
  { slug: "approved",      label_vi: "Đã duyệt",       label_en: "Approved",     cls: "bg-blue-100 text-blue-800" },
  { slug: "published",     label_vi: "Đã xuất bản",   label_en: "Published",    cls: "bg-emerald2/10 text-emerald2" },
  { slug: "needs_update",  label_vi: "Cần cập nhật",  label_en: "Needs Update", cls: "bg-orange-100 text-orange-800" },
  { slug: "archived",      label_vi: "Đã lưu trữ",    label_en: "Archived",     cls: "bg-stone-200 text-stone-700" },
  { slug: "superseded",    label_vi: "Đã thay thế",   label_en: "Superseded",   cls: "bg-rose-100 text-rose-800" },
];

const SEVERITY_COLOR = {
  critical: "bg-rose-100 text-rose-800 border-rose-300",
  high:     "bg-orange-100 text-orange-800 border-orange-300",
  medium:   "bg-amber-100 text-amber-800 border-amber-300",
  low:      "bg-yellow-50 text-yellow-700 border-yellow-200",
  none:     "bg-warmgray-50 text-warmgray-600 border-warmgray-200",
};

export default function AdminKnowledgeLibrary() {
  const { lang } = useI18n();
  const token = sessionStorage.getItem(TOKEN_KEY) || "";
  const L = (vi, en) => (lang === "en" ? en : vi);
  const headers = useMemo(() => ({ "X-Admin-Token": token }), [token]);

  const [items, setItems] = useState([]);
  const [collections, setCollections] = useState([]);
  const [meta, setMeta] = useState({ categories: [], doc_types: [], languages: [] });
  const [taxonomy, setTaxonomy] = useState({ categories: [], subcategories: {}, business_types: [], jurisdictions: [], disclaimer_levels: [], compliance_severity: [], source_types: [] });
  const [dashboard, setDashboard] = useState(null);
  const [q, setQ] = useState("");
  const [filterCat, setFilterCat] = useState("");
  const [filterLifecycle, setFilterLifecycle] = useState("");
  const [filterSeverity, setFilterSeverity] = useState("");
  const [filterJurisdiction, setFilterJurisdiction] = useState("");
  const [filterReviewDue, setFilterReviewDue] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [reviewing, setReviewing] = useState(null);
  const [toast, setToast] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [reclassifying, setReclassifying] = useState(null); // {item, suggestion, loading}

  const flash = (m) => { setToast(m); setTimeout(() => setToast(""), 2200); };

  const startReclassify = async (item) => {
    setReclassifying({ item, suggestion: null, loading: true });
    try {
      const r = await api.post(`/admin/knowledge/items/${item.id}/reclassify`, {}, { headers });
      setReclassifying({ item, suggestion: r.data, loading: false });
    } catch (err) {
      flash(err?.response?.data?.detail || "AI classifier failed");
      setReclassifying(null);
    }
  };

  const acceptReclassify = async (itemId, payload) => {
    try {
      await api.post(`/admin/knowledge/items/${itemId}/reclassify/accept`, payload, { headers });
      flash(L("Đã áp dụng phân loại AI", "AI classification applied"));
      setReclassifying(null);
      await load();
    } catch (err) {
      flash(err?.response?.data?.detail || "Apply failed");
    }
  };

  const loadDashboard = async () => {
    try {
      const r = await api.get("/admin/knowledge/dashboard", { headers });
      setDashboard(r.data);
    } catch (_err) { /* ignore */ }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (filterCat) params.category = filterCat;
      if (filterLifecycle) params.lifecycle_status = filterLifecycle;
      if (filterSeverity) params.compliance_severity = filterSeverity;
      if (filterJurisdiction) params.jurisdiction = filterJurisdiction;
      if (filterReviewDue) params.review_due = true;
      const [r1, r2, r3, r4] = await Promise.all([
        api.get("/admin/knowledge/items", { headers, params }),
        api.get("/admin/ai/collections", { headers }),
        api.get("/admin/knowledge/meta", { headers }),
        api.get("/admin/knowledge/taxonomy", { headers }),
      ]);
      setItems(r1.data.items || []);
      setCollections(r2.data.collections || []);
      setMeta(r3.data);
      setTaxonomy(r4.data || {});
    } catch (err) { flash(err?.response?.data?.detail || "Error"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); loadDashboard(); }, [filterCat, filterLifecycle, filterSeverity, filterJurisdiction, filterReviewDue]);

  const save = async (payload, id) => {
    try {
      if (id) await api.put(`/admin/knowledge/items/${id}`, payload, { headers });
      else await api.post("/admin/knowledge/items", payload, { headers });
      setEditing(null);
      flash(L("Đã lưu", "Saved"));
      load(); loadDashboard();
    } catch (err) { flash(err?.response?.data?.detail || "Error"); }
  };
  const remove = async (id) => {
    try { await api.delete(`/admin/knowledge/items/${id}`, { headers }); setConfirmDelete(null); flash(L("Đã xóa", "Deleted")); load(); loadDashboard(); }
    catch (err) { flash(err?.response?.data?.detail || "Error"); }
  };

  const transitionLifecycle = async (id, lifecycle_status, note) => {
    try {
      await api.post(`/admin/knowledge/items/${id}/lifecycle`, { lifecycle_status, note: note || null }, { headers });
      flash(L("Đã cập nhật trạng thái", "Lifecycle updated"));
      load(); loadDashboard();
      setReviewing(null);
    } catch (err) { flash(err?.response?.data?.detail || "Error"); }
  };

  return (
    <AdminShell
      testid="admin-knowledge-library"
      subtitle={L("Trí Tuệ", "Knowledge")}
      title={L("Trung tâm Rà soát Tri thức", "Knowledge Review Center")}
      actions={
        <>
          <Link to="/admin/library/knowledge/import" data-testid="kb-import-link"
            className="inline-flex items-center gap-1.5 bg-white border border-espresso/15 text-espresso rounded-full px-4 py-2 text-sm font-medium hover:bg-linen-100">
            <Upload className="w-4 h-4" /> {L("Nhập hàng loạt", "Bulk Import")}
          </Link>
          <button type="button" onClick={() => setEditing({ mode: "create", data: { ...EMPTY } })} data-testid="kb-add-btn"
            className="inline-flex items-center gap-1.5 bg-espresso text-linen rounded-full px-4 py-2 text-sm font-medium hover:bg-espresso-hover">
            <Plus className="w-4 h-4" /> {L("Thêm Tài liệu", "Add Document")}
          </button>
        </>
      }
    >
      <div className="max-w-[1400px]">
        <Toast message={toast} testId="kb-toast" />

        {dashboard && <ReviewDashboard data={dashboard} L={L} onFilterLifecycle={setFilterLifecycle} onToggleDue={() => setFilterReviewDue(v => !v)} />}

        <div className="bg-white border border-espresso/10 rounded-3xl p-4 mb-6 grid grid-cols-1 md:grid-cols-6 gap-3">
          <form onSubmit={(e) => { e.preventDefault(); load(); }} className="md:col-span-2 flex items-center gap-2 border border-espresso/10 rounded-xl px-3 py-2">
            <Search className="w-4 h-4 text-espresso-muted" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={L("Tìm theo tiêu đề, tóm tắt, từ khóa...", "Search title, summary, keywords...")} data-testid="kb-search"
              className="flex-1 bg-transparent text-sm focus:outline-none" />
          </form>
          <select value={filterCat} onChange={(e) => setFilterCat(e.target.value)} data-testid="kb-cat-filter" className="border border-espresso/10 rounded-xl px-3 py-2 text-sm bg-white">
            <option value="">{L("Tất cả danh mục", "All categories")}</option>
            {(taxonomy.categories || []).map((c) => <option key={c.slug} value={c.slug}>{lang === "en" ? c.label_en : c.label_vi}</option>)}
          </select>
          <select value={filterLifecycle} onChange={(e) => setFilterLifecycle(e.target.value)} data-testid="kb-lifecycle-filter" className="border border-espresso/10 rounded-xl px-3 py-2 text-sm bg-white">
            <option value="">{L("Tất cả trạng thái", "All lifecycles")}</option>
            {LIFECYCLE.map((l) => <option key={l.slug} value={l.slug}>{L(l.label_vi, l.label_en)}</option>)}
          </select>
          <select value={filterSeverity} onChange={(e) => setFilterSeverity(e.target.value)} data-testid="kb-severity-filter" className="border border-espresso/10 rounded-xl px-3 py-2 text-sm bg-white">
            <option value="">{L("Tất cả mức độ", "All severities")}</option>
            {(taxonomy.compliance_severity || []).map((s) => <option key={s.slug} value={s.slug}>{lang === "en" ? s.label_en : s.label_vi}</option>)}
          </select>
          <select value={filterJurisdiction} onChange={(e) => setFilterJurisdiction(e.target.value)} data-testid="kb-jurisdiction-filter" className="border border-espresso/10 rounded-xl px-3 py-2 text-sm bg-white">
            <option value="">{L("Tất cả phạm vi", "All jurisdictions")}</option>
            {(taxonomy.jurisdictions || []).map((j) => <option key={j.slug} value={j.slug}>{j.slug}</option>)}
          </select>
        </div>

        {loading ? (
          <div className="py-16 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-espresso-muted" /></div>
        ) : items.length === 0 ? (
          <div className="bg-white border border-dashed border-espresso/20 rounded-3xl p-12 text-center text-espresso-muted" data-testid="kb-empty">
            {L("Không có tài liệu nào khớp bộ lọc.", "No documents match your filters.")}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((it) => (
              <KnowledgeCard key={it.id} item={it} L={L}
                onEdit={() => setEditing({ mode: "edit", data: { ...it, tags: (it.tags || []).join(", ") } })}
                onReview={() => setReviewing(it)}
                onReclassify={() => startReclassify(it)}
                onDelete={() => setConfirmDelete(it)} />
            ))}
          </div>
        )}
      </div>

      {editing && (
        <KnowledgeEditDialog mode={editing.mode} initial={editing.data} meta={meta} taxonomy={taxonomy} collections={collections}
          onClose={() => setEditing(null)} onSave={(p) => save(p, editing.mode === "edit" ? editing.data.id : null)} L={L} />
      )}
      {reviewing && (
        <LifecycleReviewDialog item={reviewing} L={L}
          onClose={() => setReviewing(null)} onTransition={transitionLifecycle} />
      )}
      {confirmDelete && (
        <ConfirmDialog title={L("Xóa tài liệu?", "Delete document?")} body={L("Tài liệu sẽ bị xóa khỏi Trí Tuệ AI.", "This document will be removed from AI Knowledge.")}
          onCancel={() => setConfirmDelete(null)} onConfirm={() => remove(confirmDelete.id)} L={L} />
      )}
      {reclassifying && (
        <ReclassifyDialog state={reclassifying} L={L}
          onClose={() => setReclassifying(null)}
          onAccept={(payload) => acceptReclassify(reclassifying.item.id, payload)} />
      )}
    </AdminShell>
  );
}

function ReviewDashboard({ data, L, onFilterLifecycle, onToggleDue }) {
  const lc = data.by_lifecycle || {};
  const sev = data.by_severity || {};
  return (
    <div data-testid="kb-dashboard" className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
      <MetricCard testId="kb-metric-total" label={L("Tổng tri thức", "Total")} value={data.total} icon={FileText} />
      <MetricCard testId="kb-metric-draft" label={L("Bản thảo", "Draft")} value={lc.draft || 0} onClick={() => onFilterLifecycle("draft")} />
      <MetricCard testId="kb-metric-under-review" label={L("Đang rà soát", "Under Review")} value={lc.under_review || 0} icon={Clock} onClick={() => onFilterLifecycle("under_review")} />
      <MetricCard testId="kb-metric-published" label={L("Đã xuất bản", "Published")} value={lc.published || 0} icon={CheckCircle2} onClick={() => onFilterLifecycle("published")} accent="emerald" />
      <MetricCard testId="kb-metric-needs-update" label={L("Cần cập nhật", "Needs Update")} value={lc.needs_update || 0} icon={RefreshCw} onClick={() => onFilterLifecycle("needs_update")} accent="orange" />
      <MetricCard testId="kb-metric-critical" label={L("Mức độ nghiêm trọng", "Critical")} value={sev.critical || 0} icon={AlertTriangle} accent="rose" />
      <MetricCard testId="kb-metric-federal" label={L("Liên bang", "Federal")} value={data.federal_count || 0} icon={ShieldCheck} accent="blue" />
      <MetricCard testId="kb-metric-state" label={L("Tiểu bang", "State")} value={data.state_count || 0} icon={ShieldCheck} accent="blue" />
      <MetricCard testId="kb-metric-due-soon" label={L("Đến hạn 30 ngày", "Due in 30d")} value={data.review_due_soon || 0} icon={Clock} accent="amber" />
      <MetricCard testId="kb-metric-past-due" label={L("Quá hạn", "Past Due")} value={data.review_past_due || 0} icon={AlertTriangle} onClick={onToggleDue} accent="rose" />
      <MetricCard testId="kb-metric-taxes" label={L("Thuế", "Taxes")} value={(data.by_category || {}).taxes || 0} />
      <MetricCard testId="kb-metric-payroll" label={L("Lương", "Payroll")} value={(data.by_category || {}).payroll || 0} />
    </div>
  );
}

function MetricCard({ label, value, icon: Icon, accent, onClick, testId }) {
  const accentCls = {
    emerald: "border-emerald2/30 bg-emerald2/5",
    rose: "border-rose-200 bg-rose-50",
    orange: "border-orange-200 bg-orange-50",
    blue: "border-blue-200 bg-blue-50",
    amber: "border-amber-200 bg-amber-50",
  }[accent] || "border-espresso/10 bg-white";
  const Tag = onClick ? "button" : "div";
  return (
    <Tag data-testid={testId} onClick={onClick}
      className={`text-left ${accentCls} border rounded-2xl p-3 transition hover:shadow-sm ${onClick ? "cursor-pointer hover:scale-[1.01]" : ""}`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] font-semibold text-espresso-muted">
        {Icon && <Icon className="w-3 h-3" />}
        {label}
      </div>
      <div className="font-serif text-2xl text-espresso mt-1">{value}</div>
    </Tag>
  );
}

function KnowledgeCard({ item: it, L, onEdit, onReview, onReclassify, onDelete }) {
  const lc = LIFECYCLE.find((l) => l.slug === it.lifecycle_status) || LIFECYCLE[0];
  const sev = it.compliance_severity || "none";
  const isAi = it.classification_source === "ai";
  return (
    <div data-testid={`kb-card-${it.id}`} className="bg-white border border-espresso/10 rounded-3xl p-5 flex flex-col gap-2">
      <div className="flex items-center gap-2 flex-wrap">
        <FileText className="w-4 h-4 text-espresso-muted" />
        <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-emerald2">{it.doc_type || "doc"}</span>
        {it.featured && <Sparkles className="w-3.5 h-3.5 text-gold-500" />}
        {isAi && (
          <span className="text-[10px] uppercase tracking-[0.18em] font-semibold px-2 py-0.5 rounded-full bg-violet-100 text-violet-800 inline-flex items-center gap-1" data-testid={`kb-ai-classified-${it.id}`}>
            <Brain className="w-3 h-3" /> AI
          </span>
        )}
        <span data-testid={`kb-lifecycle-${it.id}`} className={`ml-auto text-[10px] uppercase tracking-[0.18em] font-semibold px-2 py-0.5 rounded-full ${lc.cls}`}>
          {L(lc.label_vi, lc.label_en)}
        </span>
      </div>
      <div className="font-serif text-lg text-espresso line-clamp-2">{it.title}</div>
      {it.summary && <p className="text-sm text-espresso-soft line-clamp-2">{it.summary}</p>}
      <div className="text-[10px] text-espresso-muted flex flex-wrap gap-1.5">
        {it.category && <span className="bg-linen-100 rounded-full px-2 py-0.5">{it.category}</span>}
        {it.jurisdiction && <span className="bg-blue-50 text-blue-800 rounded-full px-2 py-0.5">{it.jurisdiction}</span>}
        {sev !== "none" && <span className={`border rounded-full px-2 py-0.5 ${SEVERITY_COLOR[sev] || ""}`}>{sev}</span>}
        {it.language && <span className="bg-linen-100 rounded-full px-2 py-0.5">{(it.language || "").toUpperCase()}</span>}
      </div>
      {it.next_review_date && (
        <div className="text-[10px] text-espresso-muted flex items-center gap-1">
          <Clock className="w-3 h-3" /> {L("Rà soát kế tiếp:", "Next review:")} {it.next_review_date}
        </div>
      )}
      <div className="mt-2 flex items-center gap-2 flex-wrap">
        {it.file_url && <a href={it.file_url} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded-lg hover:bg-linen-100 text-espresso-soft hover:text-espresso" data-testid={`kb-open-${it.id}`}><ExternalLink className="w-4 h-4" /></a>}
        <button type="button" onClick={onReview} data-testid={`kb-review-${it.id}`} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-amber-50 text-amber-800 hover:bg-amber-100">
          <ShieldCheck className="w-3.5 h-3.5" /> {L("Rà soát", "Review")}
        </button>
        <button type="button" onClick={onReclassify} data-testid={`kb-reclassify-${it.id}`} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-violet-50 text-violet-800 hover:bg-violet-100" title={L("Phân loại lại bằng AI", "Re-classify with AI")}>
          <Brain className="w-3.5 h-3.5" /> {L("AI phân loại", "AI Classify")}
        </button>
        <button type="button" onClick={onEdit} data-testid={`kb-edit-${it.id}`} className="p-1.5 rounded-lg hover:bg-linen-100 text-espresso-soft hover:text-espresso"><Edit3 className="w-4 h-4" /></button>
        <button type="button" onClick={onDelete} data-testid={`kb-delete-${it.id}`} className="p-1.5 rounded-lg hover:bg-[#F5DADA] text-espresso-soft hover:text-[#7A2E2E]"><Trash2 className="w-4 h-4" /></button>
      </div>
    </div>
  );
}

function ReclassifyDiffRow({ label, before, after, k }) {
  const changed = (after || "") !== (before || "");
  return (
    <div className="grid grid-cols-12 items-center gap-2 text-xs" data-testid={`kb-reclassify-row-${k}`}>
      <div className="col-span-3 text-espresso-muted uppercase tracking-[0.16em] text-[10px] font-semibold">{label}</div>
      <div className={`col-span-4 truncate ${changed ? "text-warmgray-500 line-through" : "text-espresso"}`} title={String(before || "")}>
        {before || "—"}
      </div>
      <div className="col-span-1 text-center text-espresso-muted">→</div>
      <div className={`col-span-4 truncate ${changed ? "text-violet-800 font-semibold" : "text-espresso"}`} title={String(after || "")}>
        {after || "—"}
      </div>
    </div>
  );
}


function ReclassifyDialog({ state, onClose, onAccept, L }) {
  const { item, suggestion, loading } = state;
  const [edited, setEdited] = useState(null);
  // Initialize edited form from the suggestion when it arrives
  useEffect(() => {
    if (suggestion?.suggested && !edited) {
      const s = suggestion.suggested;
      setEdited({
        category: s.category || "",
        subcategory: s.subcategory || "",
        jurisdiction: s.jurisdiction || "",
        compliance_severity: s.compliance_severity || "",
        disclaimer_level: s.disclaimer_level || "",
        source_type: s.source_type || "",
        business_types: (s.business_types || []).join(", "),
        tags: (s.tags || []).join(", "),
        review_priority: s.review_priority || "",
        classification_confidence: s.confidence ?? s.classification_confidence ?? null,
        classification_reasoning: s.reasoning || s.classification_reasoning || "",
      });
    }
  }, [suggestion, edited]);

  const apply = () => {
    if (!edited) return;
    const arr = (v) => (typeof v === "string" ? v.split(",").map((s) => s.trim()).filter(Boolean) : v);
    onAccept({
      category: edited.category || null,
      subcategory: edited.subcategory || null,
      jurisdiction: edited.jurisdiction || null,
      compliance_severity: edited.compliance_severity || null,
      disclaimer_level: edited.disclaimer_level || null,
      source_type: edited.source_type || null,
      business_types: arr(edited.business_types),
      tags: arr(edited.tags),
      review_priority: edited.review_priority || null,
      classification_confidence: edited.classification_confidence ?? null,
      classification_reasoning: edited.classification_reasoning || null,
      ai_suggested_metadata: suggestion?.suggested || null,
    });
  };

  const cur = suggestion?.current || {};
  const sug = suggestion?.suggested || {};

  return (
    <DialogShell title={L("AI phân loại lại", "AI Re-classify")} onClose={onClose}
      onSubmit={(e) => { e.preventDefault(); apply(); }}
      saving={false} testIdPrefix="kb-reclassify" maxWidth="max-w-3xl"
      saveLabel={L("Áp dụng phân loại AI", "Apply AI classification")} cancelLabel={L("Hủy", "Cancel")}
      hideSubmit={loading || !suggestion}
    >
      <div data-testid="kb-reclassify-modal" className="space-y-4">
        <div className="bg-linen-100 rounded-2xl p-4">
          <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-espresso-muted">{L("Tài liệu", "Document")}</div>
          <div className="font-serif text-lg text-espresso mt-1">{item.title}</div>
          {item.summary && <p className="text-sm text-espresso-soft mt-1 line-clamp-2">{item.summary}</p>}
        </div>

        {loading && (
          <div className="text-center py-8" data-testid="kb-reclassify-loading">
            <Loader2 className="w-6 h-6 animate-spin inline text-violet-600" />
            <div className="text-xs text-espresso-muted mt-2">{L("AI đang đọc và phân loại lại tài liệu…", "AI is re-reading and re-classifying the document…")}</div>
          </div>
        )}

        {!loading && !suggestion?.suggested && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="kb-reclassify-unavailable">
            {suggestion?.message || L("AI classifier không khả dụng. Vui lòng thử lại sau.", "AI classifier unavailable. Try again later.")}
          </div>
        )}

        {!loading && suggestion?.suggested && (
          <>
            <div className="rounded-2xl border border-violet-200 bg-violet-50/40 p-4 space-y-2">
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-violet-700">
                  {L("Đối chiếu thay đổi đề xuất", "Suggested changes (current → AI)")}
                </div>
                {sug.confidence != null && (
                  <div className="text-xs font-mono text-violet-800" data-testid="kb-reclassify-confidence">
                    {L("Độ tin cậy", "Confidence")}: <span className="font-bold">{sug.confidence}%</span>
                  </div>
                )}
              </div>
              <ReclassifyDiffRow k="category" label={L("Danh mục", "Category")} before={cur.category} after={sug.category} />
              <ReclassifyDiffRow k="subcategory" label={L("Phụ mục", "Subcategory")} before={cur.subcategory} after={sug.subcategory} />
              <ReclassifyDiffRow k="jurisdiction" label={L("Phạm vi", "Jurisdiction")} before={cur.jurisdiction} after={sug.jurisdiction} />
              <ReclassifyDiffRow k="severity" label={L("Mức độ", "Severity")} before={cur.compliance_severity} after={sug.compliance_severity} />
              <ReclassifyDiffRow k="disclaimer" label={L("Disclaimer", "Disclaimer")} before={cur.disclaimer_level} after={sug.disclaimer_level} />
              <ReclassifyDiffRow k="source" label={L("Loại nguồn", "Source type")} before={cur.source_type} after={sug.source_type} />
              <ReclassifyDiffRow k="biz" label={L("Loại DN", "Business types")} before={(cur.business_types || []).join(", ")} after={(sug.business_types || []).join(", ")} />
              <ReclassifyDiffRow k="tags" label={L("Tags", "Tags")} before={(cur.tags || []).join(", ")} after={(sug.tags || []).join(", ")} />
              <ReclassifyDiffRow k="priority" label={L("Ưu tiên", "Priority")} before={cur.review_priority} after={sug.review_priority} />
            </div>

            {sug.reasoning && (
              <div className="rounded-xl border border-violet-200 bg-white p-3 text-xs text-espresso-soft" data-testid="kb-reclassify-reasoning">
                <div className="text-[10px] uppercase tracking-[0.16em] text-violet-700 mb-1 font-semibold">{L("AI giải thích", "AI reasoning")}</div>
                {sug.reasoning}
              </div>
            )}

            {edited && (
              <div className="rounded-2xl border border-espresso/10 bg-white p-4 space-y-3">
                <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-espresso-muted">
                  {L("Tùy chỉnh trước khi áp dụng (tùy chọn)", "Tweak before applying (optional)")}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                  <FieldInput label={L("Danh mục", "Category")} value={edited.category} onChange={(v) => setEdited({ ...edited, category: v })} testId="kb-reclassify-edit-category" />
                  <FieldInput label={L("Phụ mục", "Subcategory")} value={edited.subcategory} onChange={(v) => setEdited({ ...edited, subcategory: v })} testId="kb-reclassify-edit-subcategory" />
                  <FieldInput label={L("Phạm vi", "Jurisdiction")} value={edited.jurisdiction} onChange={(v) => setEdited({ ...edited, jurisdiction: v })} testId="kb-reclassify-edit-jurisdiction" />
                  <FieldInput label={L("Mức độ", "Severity")} value={edited.compliance_severity} onChange={(v) => setEdited({ ...edited, compliance_severity: v })} testId="kb-reclassify-edit-severity" />
                  <FieldInput label={L("Tags (CSV)", "Tags (CSV)")} value={edited.tags} onChange={(v) => setEdited({ ...edited, tags: v })} testId="kb-reclassify-edit-tags" className="md:col-span-2" />
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </DialogShell>
  );
}

function MetaCell({ label, value }) {
  return (
    <div className="text-sm">
      <div className="text-[10px] uppercase tracking-[0.16em] text-espresso-muted">{label}</div>
      <div className="text-espresso">{value || "—"}</div>
    </div>
  );
}

function LifecycleReviewDialog({ item, L, onClose, onTransition }) {
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  // P3 fix: ESC key dismisses the modal so it never blocks page navigation.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const act = async (status) => {
    setSaving(true);
    await onTransition(item.id, status, note);
    setSaving(false);
  };

  return (
    <DialogShell
      title={L("Hành động Rà soát Tri thức", "Knowledge Review Action")}
      onClose={onClose} onSubmit={(e) => { e.preventDefault(); }} saving={saving}
      testIdPrefix="kb-review" maxWidth="max-w-3xl" hideSubmit
    >
      <div data-testid="kb-review-modal" className="space-y-4">
        <div className="bg-linen-100 rounded-2xl p-4">
          <div className="font-serif text-xl text-espresso">{item.title}</div>
          {item.summary && <p className="text-sm text-espresso-soft mt-1">{item.summary}</p>}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <MetaCell label={L("Danh mục", "Category")} value={item.category} />
          <MetaCell label={L("Phụ mục", "Subcategory")} value={item.subcategory} />
          <MetaCell label={L("Phạm vi", "Jurisdiction")} value={item.jurisdiction} />
          <MetaCell label={L("Mức độ", "Severity")} value={item.compliance_severity} />
          <MetaCell label={L("Disclaimer", "Disclaimer")} value={item.disclaimer_level} />
          <MetaCell label={L("Loại nguồn", "Source Type")} value={item.source_type} />
          <MetaCell label={L("Nguồn", "Source")} value={item.source} />
          <MetaCell label={L("Ngày công bố", "Publication Date")} value={item.publication_date} />
          <MetaCell label={L("Phiên bản", "Version")} value={item.version} />
          <MetaCell label={L("Người rà soát", "Reviewer")} value={item.reviewed_by} />
          <MetaCell label={L("Ngày rà soát", "Reviewed At")} value={item.reviewed_at ? new Date(item.reviewed_at).toLocaleDateString() : null} />
          <MetaCell label={L("Rà soát kế", "Next Review")} value={item.next_review_date} />
          <MetaCell label={L("Loại doanh nghiệp", "Business Types")} value={(item.business_types || []).join(", ")} />
          <MetaCell label={L("Hiển thị AI", "Visible to AI")} value={item.visible_ai ? "✓" : "—"} />
          <MetaCell label={L("Công khai", "Public")} value={item.visible_public ? "✓" : "—"} />
        </div>

        {item.review_notes && (
          <div className="text-xs whitespace-pre-line bg-warmgray-50 border border-warmgray-200 rounded-xl p-3">
            <div className="text-[10px] uppercase tracking-[0.16em] text-espresso-muted mb-1">{L("Ghi chú rà soát", "Review notes")}</div>
            {item.review_notes}
          </div>
        )}

        <FieldTextarea
          label={L("Ghi chú cho hành động này (tùy chọn)", "Note for this action (optional)")}
          value={note} onChange={setNote} rows={2} testId="kb-review-note"
        />

        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 pt-2 border-t border-espresso/10">
          <button type="button" disabled={saving} onClick={() => act("approved")} data-testid="kb-action-approve"
            className="px-3 py-2 rounded-xl bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
            <CheckCircle2 className="w-4 h-4 inline mr-1" /> {L("Duyệt", "Approve")}
          </button>
          <button type="button" disabled={saving} onClick={() => act("published")} data-testid="kb-action-publish"
            className="px-3 py-2 rounded-xl bg-emerald2 text-white text-sm font-medium hover:bg-emerald2/90 disabled:opacity-50">
            <CheckCircle2 className="w-4 h-4 inline mr-1" /> {L("Xuất bản", "Publish")}
          </button>
          <button type="button" disabled={saving} onClick={() => act("under_review")} data-testid="kb-action-under-review"
            className="px-3 py-2 rounded-xl bg-amber-600 text-white text-sm font-medium hover:bg-amber-700 disabled:opacity-50">
            <Clock className="w-4 h-4 inline mr-1" /> {L("Đang rà soát", "Under Review")}
          </button>
          <button type="button" disabled={saving} onClick={() => act("needs_update")} data-testid="kb-action-needs-update"
            className="px-3 py-2 rounded-xl bg-orange-600 text-white text-sm font-medium hover:bg-orange-700 disabled:opacity-50">
            <RefreshCw className="w-4 h-4 inline mr-1" /> {L("Cần cập nhật", "Needs Update")}
          </button>
          <button type="button" disabled={saving} onClick={() => act("archived")} data-testid="kb-action-archive"
            className="px-3 py-2 rounded-xl bg-stone-600 text-white text-sm font-medium hover:bg-stone-700 disabled:opacity-50">
            <Archive className="w-4 h-4 inline mr-1" /> {L("Lưu trữ", "Archive")}
          </button>
          <button type="button" disabled={saving} onClick={() => act("superseded")} data-testid="kb-action-supersede"
            className="px-3 py-2 rounded-xl bg-rose-600 text-white text-sm font-medium hover:bg-rose-700 disabled:opacity-50">
            <GitBranch className="w-4 h-4 inline mr-1" /> {L("Đã thay thế", "Superseded")}
          </button>
          <button type="button" disabled={saving} onClick={() => act("draft")} data-testid="kb-action-draft"
            className="px-3 py-2 rounded-xl bg-warmgray-300 text-warmgray-900 text-sm font-medium hover:bg-warmgray-400 disabled:opacity-50">
            {L("Đưa về bản thảo", "Back to Draft")}
          </button>
          <button type="button" onClick={onClose} data-testid="kb-action-cancel"
            className="px-3 py-2 rounded-xl border border-espresso/15 text-espresso text-sm font-medium hover:bg-linen-100">
            {L("Đóng", "Close")}
          </button>
        </div>
      </div>
    </DialogShell>
  );
}

function KnowledgeEditDialog({ mode, initial, meta, taxonomy, collections, onClose, onSave, L }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title?.trim()) return;
    setSaving(true);
    const tags = typeof form.tags === "string" ? form.tags.split(",").map((s) => s.trim()).filter(Boolean) : (form.tags || []);
    await onSave({
      title: form.title.trim(),
      summary: form.summary || null, content: form.content || null,
      source_url: form.source_url || null, file_url: form.file_url || null,
      doc_type: form.doc_type || null, category: form.category || null, subcategory: form.subcategory || null,
      tags, search_keywords: form.search_keywords || null,
      language: form.language || "vi",
      author: form.author || null, publisher: form.publisher || null, source: form.source || null,
      version: form.version || null, effective_date: form.effective_date || null, review_date: form.review_date || null,
      status: form.status || "draft",
      visible_public: !!form.visible_public, visible_ai: !!form.visible_ai,
      visible_members: !!form.visible_members, featured: !!form.featured,
      download_allowed: !!form.download_allowed,
      collection_id: form.collection_id ? parseInt(form.collection_id, 10) : null,
      lifecycle_status: form.lifecycle_status || "draft",
      compliance_severity: form.compliance_severity || null,
      jurisdiction: form.jurisdiction || null,
      disclaimer_level: form.disclaimer_level || null,
      source_type: form.source_type || null,
      publication_date: form.publication_date || null,
    });
    setSaving(false);
  };

  const taxCats = (taxonomy.categories || []).map((c) => [c.slug, c.label_vi || c.slug]);
  const taxSubs = (taxonomy.subcategories || {})[form.category] || [];

  return (
    <DialogShell title={mode === "edit" ? L("Chỉnh sửa Tài liệu", "Edit Document") : L("Thêm Tài liệu", "Add Document")}
      onClose={onClose} onSubmit={submit} saving={saving} testIdPrefix="kb" maxWidth="max-w-4xl" saveLabel={L("Lưu", "Save")} cancelLabel={L("Hủy", "Cancel")}>
      <div className="grid md:grid-cols-2 gap-4">
        <FieldInput label={L("Tiêu đề *", "Title *")} value={form.title} onChange={(v) => set("title", v)} testId="kb-title" required className="md:col-span-2" />
        <FieldTextarea label={L("Tóm tắt", "Summary")} value={form.summary} onChange={(v) => set("summary", v)} rows={2} testId="kb-summary" className="md:col-span-2" />
        <FieldTextarea label={L("Nội dung (cho AI dùng)", "Content (the AI will use this)")} value={form.content} onChange={(v) => set("content", v)} rows={6} testId="kb-content" className="md:col-span-2" />
        <FieldSelect label={L("Bộ sưu tập", "Collection")} value={form.collection_id || ""} onChange={(v) => set("collection_id", v)} testId="kb-collection"
          options={collections.map((c) => [c.id, c.name])} />
        <FieldSelect label={L("Danh mục", "Category")} value={form.category || ""} onChange={(v) => set("category", v)} testId="kb-category" options={taxCats} />
        <FieldSelect label={L("Phụ mục", "Subcategory")} value={form.subcategory || ""} onChange={(v) => set("subcategory", v)} testId="kb-subcategory"
          options={taxSubs.map((s) => [s.slug, s.label_vi || s.slug])} />
        <FieldSelect label={L("Phạm vi", "Jurisdiction")} value={form.jurisdiction || ""} onChange={(v) => set("jurisdiction", v)} testId="kb-jurisdiction"
          options={(taxonomy.jurisdictions || []).map((j) => [j.slug, j.label_vi || j.slug])} />
        <FieldSelect label={L("Mức độ tuân thủ", "Severity")} value={form.compliance_severity || ""} onChange={(v) => set("compliance_severity", v)} testId="kb-severity-edit"
          options={(taxonomy.compliance_severity || []).map((s) => [s.slug, s.label_vi || s.slug])} />
        <FieldSelect label={L("Disclaimer", "Disclaimer")} value={form.disclaimer_level || ""} onChange={(v) => set("disclaimer_level", v)} testId="kb-disclaimer"
          options={(taxonomy.disclaimer_levels || []).map((d) => [d.slug, d.label_vi || d.slug])} />
        <FieldSelect label={L("Loại nguồn", "Source type")} value={form.source_type || ""} onChange={(v) => set("source_type", v)} testId="kb-source-type"
          options={(taxonomy.source_types || []).map((s) => [s.slug, s.label_vi || s.slug])} />
        <FieldSelect label={L("Loại tài liệu", "Doc type")} value={form.doc_type || ""} onChange={(v) => set("doc_type", v)} testId="kb-doctype"
          options={(meta.doc_types || []).map((t) => [t, t])} />
        <FieldSelect label={L("Ngôn ngữ", "Language")} value={form.language || "vi"} onChange={(v) => set("language", v)} testId="kb-language"
          options={(meta.languages || []).map((c) => [c, c.toUpperCase()])} emptyLabel="vi" />
        <FieldSelect label={L("Lifecycle", "Lifecycle")} value={form.lifecycle_status || "draft"} onChange={(v) => set("lifecycle_status", v)} testId="kb-lifecycle-edit"
          options={LIFECYCLE.map((l) => [l.slug, l.label_vi])} />
        <FieldSelect label={L("Trạng thái (legacy)", "Legacy status")} value={form.status || "draft"} onChange={(v) => set("status", v)} testId="kb-status"
          options={[["draft", "Draft"], ["published", "Published"], ["archived", "Archived"]]} emptyLabel="draft" />
        <FieldInput label={L("Tags (phân cách bằng dấu phẩy)", "Tags (comma separated)")} value={form.tags} onChange={(v) => set("tags", v)} testId="kb-tags" className="md:col-span-2" />
        <FieldInput label={L("Từ khóa tìm kiếm", "Search keywords")} value={form.search_keywords} onChange={(v) => set("search_keywords", v)} testId="kb-keywords" className="md:col-span-2" />
        <FieldInput label={L("URL nguồn", "Source URL")} value={form.source_url} onChange={(v) => set("source_url", v)} placeholder="https://..." testId="kb-source-url" />
        <FieldInput label={L("URL file", "File URL")} value={form.file_url} onChange={(v) => set("file_url", v)} placeholder="https://..." testId="kb-file-url" />
        <FieldInput label={L("Tác giả", "Author")} value={form.author} onChange={(v) => set("author", v)} testId="kb-author" />
        <FieldInput label={L("Nhà xuất bản", "Publisher")} value={form.publisher} onChange={(v) => set("publisher", v)} testId="kb-publisher" />
        <FieldInput label={L("Nguồn (IRS / OSHA / ...)", "Source (IRS / OSHA / ...)")} value={form.source} onChange={(v) => set("source", v)} testId="kb-source" />
        <FieldInput label={L("Phiên bản", "Version")} value={form.version} onChange={(v) => set("version", v)} testId="kb-version" />
        <FieldInput label={L("Ngày hiệu lực", "Effective date")} value={form.effective_date} onChange={(v) => set("effective_date", v)} placeholder="2026-01-01" testId="kb-effective" />
        <FieldInput label={L("Ngày công bố", "Publication date")} value={form.publication_date} onChange={(v) => set("publication_date", v)} placeholder="2026-01-01" testId="kb-pubdate" />
        <div className="md:col-span-2 grid grid-cols-2 md:grid-cols-5 gap-2">
          <FieldToggle label={L("AI dùng được", "Visible to AI")} checked={form.visible_ai} onChange={(v) => set("visible_ai", v)} testId="kb-vis-ai" />
          <FieldToggle label={L("Công khai", "Public")} checked={form.visible_public} onChange={(v) => set("visible_public", v)} testId="kb-vis-public" />
          <FieldToggle label={L("Thành viên", "Members")} checked={form.visible_members} onChange={(v) => set("visible_members", v)} testId="kb-vis-members" />
          <FieldToggle label={L("Nổi bật", "Featured")} checked={form.featured} onChange={(v) => set("featured", v)} testId="kb-featured" />
          <FieldToggle label={L("Cho phép tải", "Downloads")} checked={form.download_allowed} onChange={(v) => set("download_allowed", v)} testId="kb-download" />
        </div>
      </div>
    </DialogShell>
  );
}
